import { DatasetWithData, ColumnSchema, ColumnName } from '../../types';
import { MeasurementScale } from '../../types/scale.types';
import { asISODateString, asISODateTimeString, type ISODateString, type ISODateTimeString } from '../../types/core.types';
import { DatasetAccessor } from './DatasetAccessor';
import logger from '../utils/logger';

/**
 * StatisticsCalculator - 統計計算ユーティリティ
 * 
 * 各データ型に応じた統計情報の計算を行います。
 * - 数値型: 平均値、中央値、標準偏差、最小値/最大値
 * - 文字列型: ユニーク値数、最頻値、カテゴリ別件数
 * - ブール型: True/False の個数と割合
 * - 日付型: 基本情報
 */

// カテゴリ別件数を表示する最大カテゴリ数
export const MAX_CATEGORIES_FOR_BREAKDOWN = 10;

export interface NumericStats {
  count: number;
  nullCount: number;
  mean?: string;  // interval/ratio尺度のみ
  median?: string;  // ordinal/interval/ratio尺度
  std?: string;  // interval/ratio尺度のみ
  min?: string;  // ordinal/interval/ratio尺度
  max?: string;  // ordinal/interval/ratio尺度
  cv?: string;  // 変動係数 - ratio尺度のみ
  geometricMean?: string;  // 幾何平均 - ratio尺度のみ
  mode?: string;  // すべての尺度
  categoryBreakdown?: Array<{ value: string; count: number; indices: number[] }>;  // nominal尺度での頻出値（行インデックス付き）
  type: 'int64' | 'float64';
  scale?: MeasurementScale;  // 尺度情報
}

export interface StringStats {
  count: number;
  nullCount: number;
  uniqueCount: number;
  mostCommon: string;
  categoryBreakdown?: Array<{ value: string; count: number; indices: number[] }>;  // 行インデックス付き
  type: 'string';
  scale?: MeasurementScale;
}

export interface BooleanStats {
  count: number;
  nullCount: number;
  trueCount: number;
  falseCount: number;
  trueRatio: string;
  falseRatio: string;
  type: 'boolean';
  scale?: MeasurementScale;
}

export interface DateStats {
  count: number;
  nullCount: number;
  min?: ISODateTimeString | ISODateString;  // 最小日時（ISO文字列）
  max?: ISODateTimeString | ISODateString;  // 最大日時（ISO文字列）
  range?: string;  // 期間（人間が読める形式）
  type: 'datetime' | 'date';
  scale?: MeasurementScale;
}

export interface BasicStats {
  count: number;
  nullCount: number;
  type: string;
  scale?: MeasurementScale;
}

export type ColumnStats = NumericStats | StringStats | BooleanStats | DateStats | BasicStats;

/**
 * 列の統計情報を計算
 *
 * 計算量: O(n log n) ただしnは行数
 * - フィルタリングとマッピング: O(n)
 * - 数値型の統計: O(n log n)（中央値のソートによる）
 * - 文字列型の統計: O(n * m) ただしmはユニーク値の数
 * - ブール型の統計: O(n)
 *
 * @param dataset - データを持つデータセット（呼び出し側で事前にevaluateが必要）
 * @param columnName - 列名
 * @param filteredRowIndices - フィルタされた行のインデックス（オプション）
 */
export function calculateStats(
  dataset: DatasetWithData,
  columnName: ColumnName,
  filteredRowIndices?: number[] | null
): ColumnStats | null {
  const accessor = new DatasetAccessor(dataset);

  const column: ColumnSchema | undefined = dataset.schema.columns.find(col => col.name === columnName);
  if (!column) return null;

  const columnData = accessor.getColumnData(columnName);
  if (!columnData) return null;

  // フィルターが適用されている場合はフィルター済みデータのみを使用
  const relevantIndices = filteredRowIndices || Array.from({ length: dataset.rowCount || 0 }, (_, i) => i);
  const filteredColumnData = relevantIndices.map(i => columnData[i]);

  const nonNullValues = filteredColumnData.filter(val => val !== null && val !== undefined);
  const nullCount = filteredColumnData.length - nonNullValues.length;

  const scale = column.scale?.scale;
  
  if (column.type === 'int64' || column.type === 'float64') {
    return calculateNumericStats(nonNullValues as number[], nullCount, column.type, scale, relevantIndices, filteredColumnData);
  } else if (column.type === 'string') {
    return calculateStringStats(nonNullValues, nullCount, scale, relevantIndices, filteredColumnData);
  } else if (column.type === 'boolean') {
    return calculateBooleanStats(nonNullValues, nullCount, scale);
  } else if (column.type === 'datetime' || column.type === 'date') {
    return calculateDateStats(nonNullValues, nullCount, scale, column.type);
  }

  return {
    count: nonNullValues.length,
    nullCount,
    type: column.type,
    scale
  };
}

/**
 * 数値型列の統計情報を計算
 *
 * 計算量: O(n log n) ただしnは値の数
 * - モード計算: O(n)
 * - ソート（中央値、最小値、最大値用）: O(n log n)
 * - 平均・標準偏差: O(n)
 * - 幾何平均: O(n)
 */
function calculateNumericStats(
  numericValues: number[],
  nullCount: number,
  type: 'int64' | 'float64',
  scale?: MeasurementScale,
  relevantIndices?: number[],
  allColumnData?: unknown[]
): NumericStats | BasicStats {
  const validValues = numericValues.filter(val => typeof val === 'number');
  
  if (validValues.length === 0) {
    return {
      count: numericValues.length,
      nullCount,
      type,
      scale
    };
  }

  const stats: NumericStats = {
    count: numericValues.length,
    nullCount,
    type,
    scale
  };

  // int64型でnominal/ordinal尺度の場合は整数として表示するかどうか
  const shouldFormatAsInteger = type === 'int64' && (scale === 'nominal' || scale === 'ordinal');

  // モードは全ての尺度で計算
  const mode = calculateMode(validValues);
  if (mode !== null) {
    stats.mode = shouldFormatAsInteger ? mode.toString() : mode.toFixed(3);
  }

  // nominal/ordinal尺度の場合はカテゴリ別件数（上位10個）を計算
  if (scale === 'nominal' || scale === 'ordinal') {
    // 1パス目：頻度カウントのみ（行インデックスなし）
    const frequencyCount = new Map<number, number>();
    validValues.forEach(val => {
      frequencyCount.set(val, (frequencyCount.get(val) || 0) + 1);
    });

    // 頻度順にソートして上位10個を取得
    const topCategories = Array.from(frequencyCount.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, MAX_CATEGORIES_FOR_BREAKDOWN)
      .map(([value]) => value);

    // 2パス目：上位10個のカテゴリについてのみ行インデックスを収集
    const categoryData = new Map<number, { count: number; indices: number[] }>();
    topCategories.forEach(val => {
      categoryData.set(val, { count: 0, indices: [] });
    });

    if (relevantIndices && allColumnData) {
      relevantIndices.forEach((originalIndex, dataIndex) => {
        const val = allColumnData[dataIndex];
        if (typeof val === 'number' && !isNaN(val) && categoryData.has(val)) {
          const entry = categoryData.get(val)!;
          entry.count++;
          entry.indices.push(originalIndex);
        }
      });
    }

    // 上位10個を取得（既にソート済み）
    stats.categoryBreakdown = topCategories.map(value => {
      const data = categoryData.get(value)!;
      return {
        value: shouldFormatAsInteger ? value.toString() : value.toFixed(3),
        count: data.count,
        indices: data.indices
      };
    });
  }

  // nominal尺度の場合はモードとカテゴリ別件数のみ
  if (scale === 'nominal') {
    return stats;
  }

  // ordinal以上の尺度では最小値・最大値・中央値を計算
  const sortedValues = [...validValues].sort((a, b) => a - b);
  const minValue = sortedValues[0];
  const maxValue = sortedValues[sortedValues.length - 1];

  stats.min = shouldFormatAsInteger ? minValue.toString() : minValue.toFixed(3);
  stats.max = shouldFormatAsInteger ? maxValue.toString() : maxValue.toFixed(3);

  const median = sortedValues.length % 2 === 0
    ? (sortedValues[sortedValues.length / 2 - 1] + sortedValues[sortedValues.length / 2]) / 2
    : sortedValues[Math.floor(sortedValues.length / 2)];
  stats.median = shouldFormatAsInteger ? median.toString() : median.toFixed(3);

  // ordinal尺度の場合はここまで
  if (scale === 'ordinal') {
    return stats;
  }

  // interval以上の尺度では平均・標準偏差を計算
  const sum = validValues.reduce((a, b) => a + b, 0);
  const mean = sum / validValues.length;
  stats.mean = mean.toFixed(3);

  const variance = validValues.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / validValues.length;
  const std = Math.sqrt(variance);
  stats.std = std.toFixed(3);

  // ratio尺度の場合は変動係数と幾何平均も計算
  if (scale === 'ratio') {
    // 変動係数（CV）= 標準偏差 / 平均
    if (mean !== 0) {
      const cv = (std / Math.abs(mean)) * 100;
      stats.cv = cv.toFixed(2) + '%';
    }

    // 幾何平均（正の値のみ）
    const positiveValues = validValues.filter(v => v > 0);
    if (positiveValues.length > 0) {
      const product = positiveValues.reduce((acc, val) => acc * val, 1);
      const geometricMean = Math.pow(product, 1 / positiveValues.length);
      stats.geometricMean = geometricMean.toFixed(3);
    }
  }

  return stats;
}

/**
 * モード（最頻値）を計算するヘルパー関数
 *
 * 計算量: O(n) ただしnは値の数
 * - 頻度マップ構築: O(n)
 * - 最大頻度の検索: O(n)
 */
function calculateMode(values: number[]): number | null {
  if (values.length === 0) return null;
  
  const frequency = new Map<number, number>();
  values.forEach(val => {
    frequency.set(val, (frequency.get(val) || 0) + 1);
  });
  
  let maxFreq = 0;
  let mode: number | null = null;
  
  frequency.forEach((freq, val) => {
    if (freq > maxFreq) {
      maxFreq = freq;
      mode = val;
    }
  });
  
  return mode;
}

/**
 * 文字列型列の統計情報を計算
 *
 * 計算量: O(n * m) ただしnは値の数、mはユニーク値の数
 * - ユニーク値の集合構築: O(n)
 * - カテゴリ別件数の計算: O(m * n)（各ユニーク値に対してfilterを実行）
 * - ソート: O(m log m)
 */
function calculateStringStats(
  nonNullValues: unknown[],
  nullCount: number,
  scale?: MeasurementScale,
  relevantIndices?: number[],
  allColumnData?: unknown[]
): StringStats {
  const uniqueValues = new Set(nonNullValues);

  // 1パス目：頻度カウントのみ（行インデックスなし）
  const frequencyCount = new Map<string, number>();
  nonNullValues.forEach(val => {
    const strVal = String(val);
    frequencyCount.set(strVal, (frequencyCount.get(strVal) || 0) + 1);
  });

  // 頻度順にソートして上位10個を取得
  const topCategoriesWithCount = Array.from(frequencyCount.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, MAX_CATEGORIES_FOR_BREAKDOWN);

  const topCategories = topCategoriesWithCount.map(([value]) => value);
  const mostCommon = topCategoriesWithCount.length > 0
    ? `${topCategoriesWithCount[0][0]} (${topCategoriesWithCount[0][1]} times)`
    : 'N/A';

  // 2パス目：上位10個のカテゴリについてのみ行インデックスを収集
  const categoryData = new Map<string, { count: number; indices: number[] }>();
  topCategories.forEach(val => {
    categoryData.set(val, { count: 0, indices: [] });
  });

  if (relevantIndices && allColumnData) {
    relevantIndices.forEach((originalIndex, dataIndex) => {
      const val = allColumnData[dataIndex];
      if (val !== null && val !== undefined) {
        const strVal = String(val);
        if (categoryData.has(strVal)) {
          const entry = categoryData.get(strVal)!;
          entry.count++;
          entry.indices.push(originalIndex);
        }
      }
    });
  }

  // 常に上位10個のカテゴリを含める（ユニーク値が10個未満の場合は全て含める）
  const categoryBreakdown = topCategories.map(value => {
    const data = categoryData.get(value)!;
    return {
      value,
      count: data.count,
      indices: data.indices
    };
  });

  return {
    count: nonNullValues.length,
    nullCount,
    uniqueCount: uniqueValues.size,
    mostCommon,
    categoryBreakdown,
    type: 'string',
    scale
  };
}

/**
 * ブール型列の統計情報を計算
 *
 * 計算量: O(n) ただしnは値の数
 * - True件数のカウント: O(n)
 * - False件数のカウント: O(n)
 */
function calculateBooleanStats(nonNullValues: unknown[], nullCount: number, scale?: MeasurementScale): BooleanStats {
  const trueCount = nonNullValues.filter(val => val === true).length;
  const falseCount = nonNullValues.filter(val => val === false).length;

  return {
    count: nonNullValues.length,
    nullCount,
    trueCount,
    falseCount,
    trueRatio: nonNullValues.length > 0 ? (trueCount / nonNullValues.length * 100).toFixed(1) + '%' : 'N/A',
    falseRatio: nonNullValues.length > 0 ? (falseCount / nonNullValues.length * 100).toFixed(1) + '%' : 'N/A',
    type: 'boolean',
    scale
  };
}

/**
 * 日付型列の統計情報を計算
 *
 * 計算量: O(n) ただしnは値の数
 * - 最小値・最大値の計算: O(n)
 * - 期間の計算: O(1)
 */
function calculateDateStats(nonNullValues: unknown[], nullCount: number, scale?: MeasurementScale, type: 'datetime' | 'date' = 'datetime'): DateStats {
  const stats: DateStats = {
    count: nonNullValues.length,
    nullCount,
    type,
    scale
  };

  // ISO文字列をDateに変換
  const dateValues = nonNullValues
    .map(val => new Date(val as string))
    .filter(date => !isNaN(date.getTime()));

  if (dateValues.length === 0) {
    return stats;
  }

  // 最小値・最大値を計算
  const timestamps = dateValues.map(d => d.getTime());
  const minTimestamp = Math.min(...timestamps);
  const maxTimestamp = Math.max(...timestamps);

  const minDate = new Date(minTimestamp);
  const maxDate = new Date(maxTimestamp);

  // date型は日付のみ、datetime型は日時を表示（ISO形式でロケール非依存）
  if (type === 'date') {
    // YYYY-MM-DD形式
    stats.min = asISODateString(minDate.toISOString().split('T')[0]);
    stats.max = asISODateString(maxDate.toISOString().split('T')[0]);
  } else {
    // YYYY-MM-DD HH:mm:ss形式（ロケール非依存）
    // 表示用に変換しているが、元データはISO形式なのでISODateTimeStringとしてマーク
    stats.min = asISODateTimeString(minDate.toISOString().replace('T', ' ').slice(0, 19));
    stats.max = asISODateTimeString(maxDate.toISOString().replace('T', ' ').slice(0, 19));
  }

  // 期間を計算（人間が読める形式）
  const rangeMsec = maxTimestamp - minTimestamp;
  const rangeDays = Math.floor(rangeMsec / (1000 * 60 * 60 * 24));
  const rangeHours = Math.floor((rangeMsec % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const rangeMinutes = Math.floor((rangeMsec % (1000 * 60 * 60)) / (1000 * 60));

  if (rangeDays > 0) {
    stats.range = `${rangeDays} days, ${rangeHours} hours`;
  } else if (rangeHours > 0) {
    stats.range = `${rangeHours} hours, ${rangeMinutes} minutes`;
  } else if (rangeMinutes > 0) {
    stats.range = `${rangeMinutes} minutes`;
  } else {
    stats.range = `${Math.floor(rangeMsec / 1000)} seconds`;
  }

  return stats;
}

/**
 * 2つの数値列間の相関係数を計算します
 *
 * 計算量: O(n) ただしnは行数
 * - 有効なペアの抽出: O(n)
 * - 平均値の計算: O(n)
 * - 共分散と分散の計算: O(n)
 *
 * @param dataset - データを持つデータセット（呼び出し側で事前にevaluateが必要）
 * @param column1Name - 列1の名前
 * @param column2Name - 列2の名前
 * @param rowIndices - 計算対象の行インデックス（省略時は全行）
 * @returns 相関係数（-1から1の値）、計算できない場合はnull
 */
export function calculateCorrelation(
  dataset: DatasetWithData,
  column1Name: ColumnName,
  column2Name: ColumnName,
  rowIndices?: number[]
): number | null {
  const schema = dataset.schema;
  const column1 = schema.columns.find(col => col.name === column1Name);
  const column2 = schema.columns.find(col => col.name === column2Name);

  // 両列が数値型であることを確認
  if (!column1 || !column2) {
    logger.warn('[StatisticsCalculator] Column not found for correlation calculation', { column1Name, column2Name });
    return null;
  }

  if ((column1.type !== 'int64' && column1.type !== 'float64') ||
      (column2.type !== 'int64' && column2.type !== 'float64')) {
    logger.warn('[StatisticsCalculator] Non-numeric columns provided for correlation calculation', {
      column1: column1.type,
      column2: column2.type
    });
    return null;
  }

  const accessor = new DatasetAccessor(dataset);
  const column1Data = accessor.getColumnData(column1Name);
  const column2Data = accessor.getColumnData(column2Name);
  
  if (!column1Data || !column2Data) {
    logger.warn('[StatisticsCalculator] Failed to get column data for correlation calculation');
    return null;
  }
  
  const indices = rowIndices || Array.from({ length: dataset.rowCount || 0 }, (_, i) => i);
  
  // ペアワイズで両方の値が非nullの行のみを使用
  const validPairs: Array<[number, number]> = [];
  for (const index of indices) {
    const val1 = column1Data[index];
    const val2 = column2Data[index];
    if (val1 !== null && val1 !== undefined && val2 !== null && val2 !== undefined &&
        typeof val1 === 'number' && typeof val2 === 'number' &&
        !isNaN(val1) && !isNaN(val2)) {
      validPairs.push([val1, val2]);
    }
  }
  
  if (validPairs.length < 2) {
    logger.warn('[StatisticsCalculator] Not enough valid pairs for correlation calculation', {
      validPairs: validPairs.length
    });
    return null;
  }
  
  // 平均値を計算
  const mean1 = validPairs.reduce((sum, [val1]) => sum + val1, 0) / validPairs.length;
  const mean2 = validPairs.reduce((sum, [, val2]) => sum + val2, 0) / validPairs.length;
  
  // 共分散と標準偏差を計算
  let covariance = 0;
  let variance1 = 0;
  let variance2 = 0;
  
  for (const [val1, val2] of validPairs) {
    const diff1 = val1 - mean1;
    const diff2 = val2 - mean2;
    covariance += diff1 * diff2;
    variance1 += diff1 * diff1;
    variance2 += diff2 * diff2;
  }
  
  const std1 = Math.sqrt(variance1 / (validPairs.length - 1));
  const std2 = Math.sqrt(variance2 / (validPairs.length - 1));
  
  // 標準偏差が0の場合（すべて同じ値）
  if (std1 === 0 || std2 === 0) {
    logger.warn('[StatisticsCalculator] Standard deviation is zero, correlation is undefined');
    return null;
  }
  
  // 相関係数を計算
  const correlation = covariance / ((validPairs.length - 1) * std1 * std2);
  
  // -1から1の範囲に収める（浮動小数点誤差対策）
  return Math.max(-1, Math.min(1, correlation));
}
